import { useCallback, useState } from 'react'
import {
  Control,
  FieldErrors,
  FieldValues,
  FormState,
  Path,
} from 'react-hook-form'
import { DropdownProps } from 'react-native-element-dropdown/lib/typescript/components/Dropdown/model'

import FormFieldSelect from '@/components/form/FormFieldSelect'
import Species from '@/enums/species'

const DOG_MEDICATION_OPTIONS = [
  'Heartworm Prevention',
  'Flea & Tick Prevention',
  'Tri-Heart Plus',
  'Iverhart Plus',
  'Iverhart Max',
  'Imectro',
  'Interceptor',
  'MilbeGuard',
  'Interceptor Plus',
  'Sentinel',
  'Sentinel Spectrum',
  'Trifexis',
  'NexGard Plus',
  'Simparica Trio',
  'Credelio Quattro',
  'Simparica',
  'Bravecto (1 mo)',
  'Bravecto (3 mo)',
  'NexGard',
  'Credelio',
  'Heartgard',
  'Heartgard Plus',
  'Capstar',
  'Frontline Plus',
  'Revolution',

  // dewormers
  'Dewormer',
  'Panacur',
  'Safe-Guard',
  'Drontal',
  'Drontal Plus',
  'Nemex',
  'Albon',
  'Metronidazole',
  'Strongid',
  'Valbazen',

  // other meds
  'Amoxicillin',
  'Eye ointment',
].sort()

const CAT_MEDICATION_OPTIONS = [
  'Prevention',
  'Interceptor',
  'MilbeGuard',
  'Advantage Multi',
  'Barrier',
  'PARASEDGE',
  'Midamox for Cats',
  'Bravecto Plus',
  'NexGard Combo',
  'Revolution Plus',
  'Revolution',
  'Paradyne',
  'Revolt',
  'Senergy',
  'Selarid',
  'Capstar',
  'Frontline Plus',

  // dewormers
  'Dewormer',
  'Albon',
  'Drontal',
  'Drontal Cat',
  'Nemex',
  'Panacur',
  'Metronidazole',
  'Strongid',
  'Itrafungal',

  // other meds
  'Amoxicillin',
  'Eye ointment',
].sort()

interface Props<C extends FieldValues, _E extends FieldErrors> {
  species: Species | undefined
  control: Control<C, any>
  disabled?: boolean
  formState: FormState<C>
  fieldName: Path<C>
  label?: string
  isLoading?: boolean
  required?: boolean
}

type data = { label: string; value: string }

const FormFieldMedicationName = <C extends FieldValues, E extends FieldErrors>({
  species,
  control,
  disabled,
  formState,
  fieldName,
  isLoading,
  required,
  label,
  style,
}: Props<C, E> & Partial<DropdownProps<data>>) => {
  const [customMedications, setCustomMedications] = useState<string[]>([])
  const [searchText, setSearchText] = useState('')

  const medicationOptions = [
    ...(species === Species.Cat
      ? CAT_MEDICATION_OPTIONS
      : DOG_MEDICATION_OPTIONS),
    ...customMedications,
  ].map((med) => ({ label: med, value: med }))

  // Filter options based on search text
  const filteredOptions = medicationOptions.filter((item) =>
    item.label.toLowerCase().includes(searchText.toLowerCase())
  )
  const hasExactMatch = medicationOptions.some(
    (item) => item.label.toLowerCase() === searchText.toLowerCase()
  )

  // Add an option to create a new medication if search text doesn't match any existing options
  const displayOptions =
    searchText && !hasExactMatch
      ? [
          ...filteredOptions,
          {
            label: `Use "${searchText}"`,
            value: searchText,
          },
        ]
      : filteredOptions

  const handleSelectMedication = useCallback(
    (selected: data) => {
      // If it's a new medication (not in original list), add it to custom medications
      if (
        !medicationOptions.some((option) => option.value === selected.value) &&
        !customMedications.includes(selected.value)
      ) {
        setCustomMedications((prev) => [...prev, selected.value])
      }
    },
    [customMedications, medicationOptions]
  )

  return (
    <FormFieldSelect
      autoScroll
      control={control}
      data={displayOptions}
      disable={disabled}
      errors={formState.errors}
      fieldName={fieldName}
      isLoading={isLoading}
      label={label || 'Medication'}
      mode="modal"
      onChange={handleSelectMedication}
      onChangeText={setSearchText}
      required={required || false}
      search={true}
      searchField="label"
      style={style}
    />
  )
}

export default FormFieldMedicationName
